import React, { Fragment } from 'react';
import { Breakpoint } from 'react-socks';
import { makeObservable, observable, toJS } from 'mobx';
import { observer } from 'mobx-react';

import { InjectedStyledComponent, KEYS, ResponsiveComponentBase } from '../../../shared/index.js';
import { IDropdownItem } from '../Dropdown/models/IDropdownItem.js';
import { IDropdownProps } from '../Dropdown/models/index.js';
import Grid from '../Grid/Grid.js';
import { ITabProps } from '../Tab/models/ITabProps.js';
import Tab from '../Tab/Tab.js';
import Translate from '../Translate/Translate.js';

import { ITabReference, ITabsConfiguration, ITabsProps, ITabsState } from './models/index.js';
import { ITabComponent } from './models/ITabComponent.js';
import { ITabHeaderProps } from './models/ITabHeaderProps.js';
import ActiveTabHeader from './styledComponents/ActiveTabHeader.js';
import Dropdown from './styledComponents/Dropdown.js';
import Header from './styledComponents/Header.js';
import Pane from './styledComponents/Pane.js';
import TabHeader from './styledComponents/TabHeader.js';
import TabHeaderIcon from './styledComponents/TabHeaderIcon.js';
import Wrapper from './styledComponents/Wrapper.js';
import { TABS_CATEGORY_KEY, TABS_COMPONENT_KEY } from './Tabs.meta.js';

const NAVIGATION_KEYS = [KEYS.LEFT_ARROW, KEYS.RIGHT_ARROW, KEYS.UP_ARROW, KEYS.DOWN_ARROW, KEYS.HOME, KEYS.END];

const ACCEPT_KEYS = [KEYS.ENTER, KEYS.SPACE, KEYS.SPACEBAR];

interface ITabHeaderOptions {
	// eslint-disable-next-line @typescript-eslint/naming-convention
	TabHeaderComponent: any;
	tabsProps: ITabsProps;
	indexOfTab: number;
	tabProps: ITabProps;
	breakpointIndex?: number;
}

interface IHandleKeyDownOptions {
	e: React.KeyboardEvent<HTMLLIElement> | React.KeyboardEvent<HTMLDivElement>;
	tabsProps: ITabsProps;
	tabProps: ITabProps;
	breakpointIndex: number;
}

export class Tabs extends ResponsiveComponentBase<ITabsProps, ITabsState, ITabsConfiguration> {
	categoryKey = TABS_CATEGORY_KEY;
	componentKey = TABS_COMPONENT_KEY;

	tabElements: any[][];
	focusActiveTab = false;
	activeTabs: ITabReference[] = [];
	onChangeEvents: (((tab: ITabReference) => void) | undefined)[] = [];

	get activeTabKey() {
		return this.activeTabs;
	}

	constructor(props: ITabsProps) {
		super(props);

		this.tabElements = this.initializeTabElementsArray();
		this.onChangeEvents = this.initializeOnChangeEvents();

		makeObservable(this, {
			activeTabs: observable,
		});
	}

	initializeTabElementsArray() {
		return this.breakpoints.reduce((tabElements, breakpoint) => {
			const { props } = breakpoint;
			if (props) {
				const tabs = this.getTabs(props);
				tabElements.push(Array.from({ length: tabs.length }));
			}
			return tabElements;
		}, [] as any[][]);
	}

	initializeOnChangeEvents() {
		return this.breakpoints.reduce((onChangeEvents, breakpoint) => {
			const { props } = breakpoint;
			if (props) {
				onChangeEvents.push(props.onChange);
			}
			return onChangeEvents;
		}, [] as (((activeTab: ITabReference) => void) | undefined)[]);
	}

	assignReferenceToTabElements = (tabInstance: any | null, breakpointIndex: number, indexOfTab: number) => {
		if (this.tabElements && this.tabElements[breakpointIndex]) {
			this.tabElements[breakpointIndex][indexOfTab] = tabInstance;
		}
	};

	handleNavigation(key: keyof KEYS, currentTabIndex: number, breakpointIndex: number) {
		const lastIndex = this.tabElements[breakpointIndex].length - 1;
		let nextIndex = currentTabIndex;
		switch (key) {
			case KEYS.LEFT_ARROW:
			case KEYS.UP_ARROW:
				nextIndex -= 1;
				break;
			case KEYS.RIGHT_ARROW:
			case KEYS.DOWN_ARROW:
				nextIndex += 1;
				break;
			case KEYS.HOME:
				nextIndex = 0;
				break;
			case KEYS.END:
				nextIndex = lastIndex;
				break;
			default:
				break;
		}
		if (nextIndex < 0) {
			nextIndex = lastIndex;
		}
		if (nextIndex > lastIndex) {
			nextIndex = 0;
		}
		this.focusTab(breakpointIndex, nextIndex);
	}

	handleKeyDown = (options: IHandleKeyDownOptions) => {
		const { e, tabProps, breakpointIndex } = options;
		const key: any = e.key;
		const indexOfTriggeringTab: number = parseInt(e.currentTarget.id, 10);
		if (NAVIGATION_KEYS.indexOf(key as KEYS) !== -1) {
			e.preventDefault();
			e.stopPropagation();
			this.handleNavigation(key, indexOfTriggeringTab, breakpointIndex);
		} else if (ACCEPT_KEYS.indexOf(key as KEYS) !== -1) {
			e.preventDefault();
			this.setActiveTab(breakpointIndex, {
				index: indexOfTriggeringTab,
				key: tabProps.tabKey,
				label: tabProps.header,
			});
		}
	};

	focusTab(breakpointIndex: number, index: number) {
		if (
			this.tabElements &&
			this.tabElements[breakpointIndex] &&
			this.tabElements[breakpointIndex].length - 1 >= index
		) {
			this.tabElements[breakpointIndex][index].focus();
		}
	}

	getActiveTabIndex(breakpointIndex: number): number | undefined {
		const activeTabRef = this.activeTabs[breakpointIndex];
		if (activeTabRef) {
			return activeTabRef.index;
		}
	}

	getActiveTabKey(breakpointIndex: number): string | undefined {
		const activeTabRef = this.activeTabs[breakpointIndex];
		if (activeTabRef && activeTabRef.key) {
			return activeTabRef.key;
		}
	}

	setActiveTab(breakpointIndex: number, tab: ITabReference, disableFocus = false, logDataLayer = true) {
		const activeTabs = toJS(this.activeTabs);
		activeTabs[breakpointIndex] = tab;
		this.activeTabs = activeTabs;
		const onChange = this.onChangeEvents[breakpointIndex];
		if (onChange && logDataLayer) {
			onChange(tab);
		}
		if (!disableFocus) {
			this.focusActiveTab = true;
		}
	}

	setDefaultActiveTab(props: ITabsProps, breakpointIndex: number) {
		const { activeTabKey } = props;
		let { active } = props;
		let label;
		if (this.activeTabs[breakpointIndex] !== undefined && !activeTabKey) {
			return;
		}

		if (activeTabKey) {
			active = activeTabKey;
		}

		const tabComponents = this.getTabs(props);
		if (!active && tabComponents.length > 0) {
			const firstTab = this.getTabs(props)[0];
			active = firstTab.tabKey;
			label = firstTab.header;
		}
		const index = tabComponents.findIndex((t) => t.tabKey === active);
		this.setActiveTab(breakpointIndex, { index, key: active, label }, true, false);
	}

	isTabActive(tab: ITabComponent, indexOfTab: number, breakpointIndex: number) {
		const activeBreakpointTabRef = this.activeTabs[breakpointIndex];
		return activeBreakpointTabRef.key === tab.tabKey || activeBreakpointTabRef.index === indexOfTab;
	}

	getActiveTab(props: ITabsProps, breakpointIndex: number): ITabComponent | undefined {
		const activeTabs = this.getTabs(props).filter((tab: ITabComponent, indexOfTab: number) => {
			return this.isTabActive(tab, indexOfTab, breakpointIndex);
		});
		if (activeTabs && activeTabs.length > 0) {
			return activeTabs[0];
		}
	}

	getActiveTabContent(props: ITabsProps, breakpointIndex: number) {
		const activeTab = this.getActiveTab(props, breakpointIndex);
		if (activeTab) {
			return this.getTabContent(activeTab);
		}
	}

	getTabContent(tab: ITabComponent) {
		const { grid, content } = tab;
		if (grid) {
			return <Grid {...grid} />;
		}
		return content;
	}

	getTabs(props: ITabsProps): ITabComponent[] {
		const { children, tabComponents } = props;
		if (tabComponents) {
			return tabComponents;
		}
		if (children) {
			const tabs = React.Children.map(children, (child: any) => {
				if (React.isValidElement(child) && child.type === Tab) {
					const { children: content, ...props } = child.props as any;
					return {
						content,
						...props,
					};
				}
			});
			return tabs;
		}
		return [];
	}

	getTabHeaders(props: ITabsProps, breakpointIndex: number) {
		const { headersMode = 'tabs' } = props;
		const tabs = this.getTabs(props);
		if (headersMode === 'tabs') {
			return tabs.map((tab: ITabProps, indexOfTab: number) => {
				const TabHeaderComponent = this.isTabActive(tab, indexOfTab, breakpointIndex)
					? ActiveTabHeader
					: TabHeader;
				return this.getTabHeader({
					breakpointIndex,
					indexOfTab,
					TabHeaderComponent,
					tabProps: tab,
					tabsProps: props,
				});
			});
		}
		if (headersMode === 'dropdown') {
			const items = tabs.map((tab) => {
				const item: IDropdownItem = {
					label: tab.header || '',
					value: tab.tabKey,
				};
				return item;
			});
			const activeTabKey = this.getActiveTabKey(breakpointIndex);
			const dropdownProps: IDropdownProps = {
				items,
				selectedItem: items.find((item) => item.value === activeTabKey),
				onChange: (selection: IDropdownItem | IDropdownItem[], index = -1) => {
					if (!Array.isArray(selection)) {
						this.setActiveTab(breakpointIndex, { index, key: selection.value, label: selection.label });
					}
				},
			};
			return <Dropdown {...dropdownProps} />;
		}
		return null;
	}

	getTabHeader(options: ITabHeaderOptions) {
		const {
			// eslint-disable-next-line @typescript-eslint/naming-convention
			TabHeaderComponent,
			tabsProps,
			tabProps,
			breakpointIndex,
			indexOfTab = 0,
		} = options;
		const { header, icon, tabKey } = tabProps;
		const iconAlign = (icon && icon.align) || 'left';

		let tabHeaderProps: Partial<ITabHeaderProps> = {
			id: tabKey,
			key: tabKey,
			role: 'tab',
		};

		// if rendering the NormalView, then the tabs are interactable  and thus need to keep the breakpoint index in mind
		if (breakpointIndex !== undefined) {
			const isActive = indexOfTab === this.getActiveTabIndex(breakpointIndex);
			tabHeaderProps = {
				...tabHeaderProps,
				tabIndex: isActive ? 0 : -1,
				onClick: () =>
					this.setActiveTab(breakpointIndex, {
						index: indexOfTab,
						key: tabKey,
						label: header,
					}),
				onKeyDown: (e: any) =>
					this.handleKeyDown({
						e,
						tabProps,
						tabsProps,
						breakpointIndex,
					}),
				assignTabReference: (instance: any) => {
					this.assignReferenceToTabElements(instance, breakpointIndex, indexOfTab);
				},
			};

			if (this.focusActiveTab && isActive) {
				tabHeaderProps = { ...tabHeaderProps, isFocused: true };
				this.focusActiveTab = false;
			}
		}

		return (
			<TabHeaderComponent {...tabHeaderProps}>
				{icon && iconAlign === 'left' && <TabHeaderIcon asset={icon.asset} />}
				<Translate>{header}</Translate>
				{icon && iconAlign === 'right' && <TabHeaderIcon asset={icon.asset} />}
			</TabHeaderComponent>
		);
	}

	renderFlatView(props: ITabsProps) {
		const { className } = this.props;
		const tabs = this.getTabs(props).map((tabProps: ITabProps, indexOfTab: number) => {
			return (
				<Wrapper className={className} key={tabProps.tabKey}>
					<Header>
						{this.getTabHeader({
							indexOfTab,
							tabProps,
							tabsProps: props,
							// eslint-disable-next-line @typescript-eslint/naming-convention
							TabHeaderComponent: ActiveTabHeader,
						})}
					</Header>
					<Pane>{this.getTabContent(tabProps)}</Pane>
				</Wrapper>
			);
		});
		return tabs;
	}

	renderNormalView(props: ITabsProps, breakpointIndex: number) {
		const { className, tabListKey } = this.props;
		const activeTab = this.getActiveTab(props, breakpointIndex);
		const activeTabKey = activeTab && activeTab.tabKey;
		let headerProps = {};
		if (tabListKey) {
			headerProps = {
				'aria-labelledby': tabListKey,
			};
		}
		return (
			<Wrapper className={className}>
				<Header role="tablist" {...headerProps}>
					{this.getTabHeaders(props, breakpointIndex)}
				</Header>
				<Pane role="tabpanel" tabIndex={0} aria-labelledby={activeTabKey}>
					{this.getActiveTabContent(props, breakpointIndex)}
				</Pane>
			</Wrapper>
		);
	}

	render() {
		const breakpoints = this.breakpoints;
		return (
			<Fragment>
				{breakpoints.map((breakpoint, breakpointIndex) => {
					const { props } = breakpoint;
					if (props) {
						const flatView =
							(breakpoint && breakpoint.props && breakpoint.props.flatView) ||
							this.props.flatView ||
							false;
						const breakpointProps = this.getBreakpointComponentProps(breakpoint, breakpointIndex);
						this.setDefaultActiveTab(props, breakpointIndex);

						return (
							<Breakpoint {...breakpointProps}>
								{flatView ? this.renderFlatView(props) : this.renderNormalView(props, breakpointIndex)}
							</Breakpoint>
						);
					}
				})}
			</Fragment>
		);
	}
}

export default InjectedStyledComponent(observer(Tabs), TABS_CATEGORY_KEY, TABS_COMPONENT_KEY);
